<template>
  <div class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Create Hospital Settlement</h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>

        <div class="modal-body">
          <!-- Hospital Selection -->
          <div class="row mb-4">
            <div class="col-md-6">
              <label class="form-label">Select Hospital <span class="text-danger">*</span></label>
              <select class="form-select" v-model="selectedHospitalId" @change="onHospitalChange">
                <option value="">Choose a hospital...</option>
                <option v-for="hospital in hospitals" :key="hospital.hospital_id"
                        :value="hospital.hospital_id">
                  {{ hospital.hospital_name }} - {{ hospital.woreda_name }}
                </option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label">Period Start <span class="text-danger">*</span></label>
              <input type="date" class="form-control" v-model="periodStart"
                     :max="maxDate" @change="loadEligibleClaims">
            </div>
            <div class="col-md-3">
              <label class="form-label">Period End <span class="text-danger">*</span></label>
              <input type="date" class="form-control" v-model="periodEnd"
                     :min="periodStart" :max="maxDate" @change="loadEligibleClaims">
            </div>
          </div>

          <!-- Hospital Info -->
          <div v-if="selectedHospital" class="card border mb-4">
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <h6 class="text-primary mb-3">Hospital Information</h6>
                  <p class="mb-1"><strong>License Number:</strong> {{ selectedHospital.license_number }}</p>
                  <p class="mb-1"><strong>Location:</strong> {{ selectedHospital.region_name }}, {{ selectedHospital.woreda_name }}</p>
                  <p class="mb-0"><strong>Category:</strong> {{ selectedHospital.category_name }}</p>
                </div>
                <div class="col-md-6">
                  <h6 class="text-primary mb-3">Banking Details</h6>
                  <p class="mb-1"><strong>Bank:</strong> {{ selectedHospital.bank_name || 'Not provided' }}</p>
                  <p class="mb-1"><strong>Account:</strong> {{ selectedHospital.bank_account_number || 'Not provided' }}</p>
                  <p class="mb-0"><strong>Account Holder:</strong> {{ selectedHospital.account_holder_name || 'Not provided' }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loadingClaims" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3">Loading eligible claims...</p>
          </div>

          <!-- No Claims -->
          <div v-else-if="eligibleClaims.length === 0 && selectedHospitalId && periodStart && periodEnd"
               class="alert alert-info">
            <i class="bx bx-info-circle me-2"></i>
            No approved claims found for settlement in the selected period.
          </div>

          <!-- Eligible Claims -->
          <div v-else-if="eligibleClaims.length > 0" class="card border">
            <div class="card-header bg-light d-flex justify-content-between align-items-center">
              <h6 class="mb-0">Eligible Claims for Settlement</h6>
              <div>
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" id="selectAllClaims"
                         v-model="selectAll" @change="toggleSelectAll">
                  <label class="form-check-label" for="selectAllClaims">
                    Select All ({{ eligibleClaims.length }} claims)
                  </label>
                </div>
              </div>
            </div>
            <div class="card-body">
              <div class="table-responsive" style="max-height: 400px; overflow-y: auto;">
                <table class="table table-sm">
                  <thead>
                  <tr>
                    <th>
                      <div class="form-check">
                        <input class="form-check-input" type="checkbox" disabled>
                      </div>
                    </th>
                    <th>Claim Number</th>
                    <th>Member</th>
                    <th>Service Date</th>
                    <th>Approval Date</th>
                    <th>Type</th>
                    <th class="text-end">Approved Amount</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="claim in eligibleClaims" :key="claim.claim_id">
                    <td>
                      <div class="form-check">
                        <input class="form-check-input" type="checkbox"
                               v-model="selectedClaimIds" :value="claim.claim_id">
                      </div>
                    </td>
                    <td>{{ claim.claim_number }}</td>
                    <td>
                      <div>
                        <div>{{ claim.first_name }} {{ claim.surname }}</div>
                        <small class="text-muted">{{ claim.cbhi_number }}</small>
                      </div>
                    </td>
                    <td>{{ formatDate(claim.service_date) }}</td>
                    <td>{{ formatDate(claim.approval_date) }}</td>
                    <td>{{ claim.type_name }}</td>
                    <td class="text-end fw-bold">ETB {{ formatAmount(claim.approved_amount) }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Settlement Summary -->
          <div v-if="selectedClaimIds.length > 0" class="card border mt-4">
            <div class="card-header bg-primary text-white">
              <h6 class="mb-0">Settlement Summary</h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-4 text-center">
                  <h4 class="mb-0">{{ selectedClaimIds.length }}</h4>
                  <p class="text-muted mb-0">Selected Claims</p>
                </div>
                <div class="col-md-4 text-center">
                  <h4 class="mb-0 text-primary">ETB {{ formatAmount(totalSelectedAmount) }}</h4>
                  <p class="text-muted mb-0">Total Amount</p>
                </div>
                <div class="col-md-4 text-center">
                  <h4 class="mb-0">{{ formatDate(periodStart) }} - {{ formatDate(periodEnd) }}</h4>
                  <p class="text-muted mb-0">Settlement Period</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Previous Settlements Warning -->
          <div v-if="recentSettlements.length > 0" class="alert alert-warning mt-3">
            <i class="bx bx-info-circle me-2"></i>
            <strong>Note:</strong> This hospital has {{ recentSettlements.length }} recent settlement(s).
            Last settlement was on {{ formatDate(recentSettlements[0].created_at) }}
            for ETB {{ formatAmount(recentSettlements[0].total_amount) }}.
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">
            <i class="bx bx-x me-1"></i>Cancel
          </button>
          <button type="button" class="btn btn-primary" @click="createSettlement"
                  :disabled="!canCreateSettlement || processing">
            <span v-if="processing">
              <span class="spinner-border spinner-border-sm me-2" role="status"></span>
              Processing...
            </span>
            <span v-else>
              <i class="bx bx-check me-1"></i>Create Settlement
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { authUrl, executeJson } from "@/api";

export default {
  name: "SettlementModal",
  data() {
    return {
      hospitals: [],
      selectedHospitalId: '',
      selectedHospital: null,
      periodStart: '',
      periodEnd: '',
      eligibleClaims: [],
      selectedClaimIds: [],
      selectAll: false,
      recentSettlements: [],
      loadingClaims: false,
      processing: false
    };
  },
  computed: {
    maxDate() {
      return new Date().toISOString().split('T')[0];
    },

    totalSelectedAmount() {
      return this.eligibleClaims
          .filter(claim => this.selectedClaimIds.includes(claim.claim_id))
          .reduce((sum, claim) => sum + parseFloat(claim.approved_amount || 0), 0);
    },

    canCreateSettlement() {
      return this.selectedHospitalId &&
          this.periodStart &&
          this.periodEnd &&
          this.selectedClaimIds.length > 0 &&
          !this.processing;
    }
  },
  mounted() {
    this.loadHospitals();
    this.setDefaultDates();
  },
  methods: {
    async loadHospitals() {
      const data = {
        "function": "getHospitals",
        "status": "active",
        "limit": 1000
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.hospitals = res.data.data.hospitals || [];
        }
      } catch (e) {
        console.error("Failed to load hospitals:", e);
      }
    },

    setDefaultDates() {
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      this.periodStart = firstDay.toISOString().split('T')[0];
      this.periodEnd = lastDay.toISOString().split('T')[0];
    },

    onHospitalChange() {
      this.selectedHospital = this.hospitals.find(h => h.hospital_id == this.selectedHospitalId);
      this.eligibleClaims = [];
      this.selectedClaimIds = [];

      if (this.selectedHospitalId) {
        this.loadEligibleClaims();
        this.loadRecentSettlements();
      }
    },

    async loadEligibleClaims() {
      if (!this.selectedHospitalId || !this.periodStart || !this.periodEnd) return;

      this.loadingClaims = true;
      this.eligibleClaims = [];
      this.selectedClaimIds = [];

      const data = {
        "function": "getClaims",
        "hospitalId": this.selectedHospitalId,
        "status": "approved",
        "startDate": this.periodStart,
        "endDate": this.periodEnd,
        "limit": 1000
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          // Filter out claims that are already in settlements
          this.eligibleClaims = (res.data.data.claims || []).filter(claim =>
              claim.status === 'approved' && !claim.settlement_batch_id
          );
        }
      } catch (e) {
        console.error("Failed to load eligible claims:", e);
      } finally {
        this.loadingClaims = false;
      }
    },

    async loadRecentSettlements() {
      const data = {
        "function": "getSettlementBatches",
        "hospitalId": this.selectedHospitalId,
        "limit": 5
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.recentSettlements = res.data.data.batches || [];
        }
      } catch (e) {
        console.error("Failed to load recent settlements:", e);
      }
    },

    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedClaimIds = this.eligibleClaims.map(c => c.claim_id);
      } else {
        this.selectedClaimIds = [];
      }
    },

    async createSettlement() {
      if (!this.canCreateSettlement) return;

      this.processing = true;

      const data = {
        "function": "createHospitalSettlement",
        "hospitalId": this.selectedHospitalId,
        "periodStart": this.periodStart,
        "periodEnd": this.periodEnd,
        "claimIds": this.selectedClaimIds
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.$emit('created', res.data.data);
        } else {
          alert(res.data.message || 'Failed to create settlement');
        }
      } catch (e) {
        console.error("Failed to create settlement:", e);
        alert('Failed to create settlement. Please try again.');
      } finally {
        this.processing = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatAmount(amount) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount || 0);
    }
  },
  watch: {
    selectedClaimIds(newVal) {
      this.selectAll = newVal.length === this.eligibleClaims.length && this.eligibleClaims.length > 0;
    }
  }
};
</script>

<style scoped>
.modal.show {
  display: block !important;
}

.table-responsive {
  border: 1px solid #dee2e6;
}

.form-check-input {
  cursor: pointer;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.2rem;
}
</style>